import React, { useContext, useMemo } from 'react';
import { useAuthContext } from './AuthContextProvider';
import { RawAccountEntitiesContext, useDataContext } from './DataContextProvider';
import { DataContextType } from './types';
import { createFieldMap, createFieldMapType } from './useEntities';

// Holds only the merged accountEntities (raw aggProjs + externalProjs merged in).
// Isolated so that re-renders of DataContext don't invalidate it.
const MergedAccountEntitiesContext = React.createContext<DataContextType['accountEntities'] | null>(null);

export const AvDataContextWithExternalProjectionsProvider = ({ children }) => {
  const rawAccountEntities = useContext(RawAccountEntitiesContext);

  const mergedAccountEntities = useMemo<DataContextType['accountEntities']>(
    () =>
      rawAccountEntities
        ? {
            ...rawAccountEntities,
            aggProjs: { ...rawAccountEntities.aggProjs, ...rawAccountEntities.externalProjs },
            fieldMap: { ...rawAccountEntities.fieldMap, ...createFieldMap(rawAccountEntities.externalProjs) },
            fieldTypeMap: { ...rawAccountEntities.fieldTypeMap, ...createFieldMapType(rawAccountEntities.externalProjs) },
          }
        : ({} as DataContextType['accountEntities']),
    [rawAccountEntities]
  );

  return <MergedAccountEntitiesContext.Provider value={mergedAccountEntities}>{children}</MergedAccountEntitiesContext.Provider>;
};

export const useAvContext = () => {
  const authContext = useAuthContext();
  const dataContext = useDataContext();
  const mergedAccountEntities = useContext(MergedAccountEntitiesContext);

  return useMemo(
    () => ({
      ...authContext,
      ...dataContext,
      isLoading: authContext.isLoading || dataContext.isLoading,
      // Override accountEntities with the merged version when inside
      // AvDataContextWithExternalProjectionsProvider, otherwise use the base.
      accountEntities: mergedAccountEntities ?? dataContext.accountEntities,
    }),
    [authContext, dataContext, mergedAccountEntities]
  );
};
